import { useQuery } from '@tanstack/react-query'
import * as api from '../services/reportDetailApi'
import { getAuditLog } from '../services/auditLogApi'
import type { AuditLogQuery } from '../types/auditLog'
import { adminPageMayFetch } from '../utils/adminGate'
import { errorKind } from '../utils/format'
import type { ReportDetailTab, ReportDetailWindow } from '../utils/reportDetailPath'
import { useLighthouseAdmin } from './useLighthouseAdmin'

/**
 * Report Detail's queries: one hook per server-read tab, plus the History tab's
 * audit trail. (Access is hooks/useReportAccess.ts, unchanged; the Governance
 * tab's episodes are hooks/useRetirementEpisodes.ts with `reportId`.)
 *
 * EACH ONE FETCHES ONLY WHILE ITS TAB IS OPEN — `open` — AND the gate allows an
 * admin page to fetch (`adminPageMayFetch`): never on `denied`, never on
 * `pending` (the catalogue's answer is on its way, and a request sent now races a
 * refusal), and on `unknown` (a missing answer must never lock an administrator
 * out; the endpoint's own 403 then renders restricted). The page mounts a tab's
 * body only while that tab is open, and the Overview query is the header's too,
 * so the page keeps that one open on every tab.
 *
 * KEY: `['observatory', 'reportDetail', reportId, tab, days]`, with `days` null
 * for the three tabs that have no window, so a window change can never be served
 * the previous window's figures.
 *
 * NO POLL, for the reason useRegistry gives: nothing here moves on a clock a poll
 * could keep up with, and the Operations read sorts up to 50,000 durations. Each
 * response carries its own `evaluatedAt`, which the tab shows.
 *
 * NO RETRY on a refusal a second try cannot change — 403, 401, 404 and 400.
 */

/** The query key every Report Detail read is filed under. */
export function reportDetailKey(
  reportId: string,
  tab: Exclude<ReportDetailTab, 'access' | 'history'>,
  days: ReportDetailWindow | null,
): readonly unknown[] {
  return ['observatory', 'reportDetail', reportId, tab, days]
}

/** Retry only what a second try might change: never a 403, 401, 404 or 400; anything else once. */
export function shouldRetryReportDetail(failureCount: number, error: unknown): boolean {
  const kind = errorKind(error)
  if (kind === 'forbidden' || kind === 'unauthorized' || kind === 'not-found' || kind === 'bad-request') return false
  return failureCount < 1
}

/** Whether a tab's read may go out: its tab is open, there is an id, and the gate lets an admin page fetch. */
function useMayFetch(reportId: string, open: boolean): boolean {
  const gate = useLighthouseAdmin()
  return open && Boolean(reportId) && adminPageMayFetch(gate)
}

interface TabOptions {
  /** Whether this hook's tab (or, for Overview, the header) is on screen. */
  open: boolean
}

/** Overview — and the page header, which reads the same cache entry. */
export function useReportDetailOverview(reportId: string, { open }: TabOptions) {
  const enabled = useMayFetch(reportId, open)
  return useQuery({
    queryKey: reportDetailKey(reportId, 'overview', null),
    queryFn: () => api.getReportDetailOverview(reportId),
    enabled,
    retry: shouldRetryReportDetail,
  })
}

export function useReportDetailGovernance(reportId: string, { open }: TabOptions) {
  const enabled = useMayFetch(reportId, open)
  return useQuery({
    queryKey: reportDetailKey(reportId, 'governance', null),
    queryFn: () => api.getReportDetailGovernance(reportId),
    enabled,
    retry: shouldRetryReportDetail,
  })
}

export function useReportDetailOperations(reportId: string, days: ReportDetailWindow, { open }: TabOptions) {
  const enabled = useMayFetch(reportId, open)
  return useQuery({
    queryKey: reportDetailKey(reportId, 'operations', days),
    queryFn: () => api.getReportDetailOperations(reportId, days),
    enabled,
    retry: shouldRetryReportDetail,
  })
}

export function useReportDetailUsage(reportId: string, days: ReportDetailWindow, { open }: TabOptions) {
  const enabled = useMayFetch(reportId, open)
  return useQuery({
    queryKey: reportDetailKey(reportId, 'usage', days),
    queryFn: () => api.getReportDetailUsage(reportId, days),
    enabled,
    retry: shouldRetryReportDetail,
  })
}

export function useReportDetailScheduling(reportId: string, { open }: TabOptions) {
  const enabled = useMayFetch(reportId, open)
  return useQuery({
    queryKey: reportDetailKey(reportId, 'scheduling', null),
    queryFn: () => api.getReportDetailScheduling(reportId),
    enabled,
    retry: shouldRetryReportDetail,
  })
}

/** The History tab's page size: the Audit Log page's own. */
export const REPORT_HISTORY_PAGE_SIZE = 50

/** The audit query for one page of one report's trail — exactly what the Audit Log page sends for `?entityId=`. */
export function reportHistoryQuery(reportId: string, page: number): AuditLogQuery {
  return { entityId: reportId, skip: (Math.max(1, page) - 1) * REPORT_HISTORY_PAGE_SIZE, top: REPORT_HISTORY_PAGE_SIZE }
}

/**
 * The History tab: the audit trail for this report, through the existing
 * `entityId` filter.
 *
 * SHARES THE AUDIT LOG PAGE'S KEY, `['audit-log', query]`: the page's query for
 * `/audit?entityId=<id>` hashes to the same entry (its unset filters are
 * undefined, and undefined members do not enter the hash), so "Open in Audit
 * Log" lands on rows already fetched. LighthouseAdmin at class level on the
 * server, so gated like every Report Detail read.
 */
export function useReportHistory(reportId: string, page: number, { open }: TabOptions) {
  const enabled = useMayFetch(reportId, open)
  const query = reportHistoryQuery(reportId, page)
  return useQuery({
    queryKey: ['audit-log', query],
    queryFn: () => getAuditLog(query),
    enabled,
    retry: shouldRetryReportDetail,
  })
}
